// frontend/src/pages/BulkUpload.jsx
import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FaUpload, FaDownload, FaFileExcel, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';

const BulkUpload = () => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [results, setResults] = useState(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const allowedTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
            if (allowedTypes.includes(selectedFile.type)) {
                setFile(selectedFile);
                toast.success(`File selected: ${selectedFile.name}`);
            } else {
                toast.error('Please select a CSV or Excel file');
                e.target.value = null;
            }
        }
    };

    const downloadTemplate = async () => {
        try {
            const response = await axios.get('/api/bulk-upload/template', {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'task_upload_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Template downloaded successfully');
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Please select a file first');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post('/api/bulk-upload/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                setResults(response.data.results);
                toast.success(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Bulk Upload Tasks</h1>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Panel - Upload Section */}
                <div className="glass-card p-6">
                    <h2 className="text-xl font-semibold mb-4">Upload Tasks</h2>
                    <p className="text-gray-600 mb-4">
                        Upload CSV or Excel file with your tasks. Download the template first to see the required format.
                    </p>
                    
                    <div className="space-y-4">
                        <button
                            onClick={downloadTemplate}
                            className="w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition-all flex items-center justify-center gap-2"
                        >
                            <FaDownload /> Download Template
                        </button>
                        
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            <input
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleFileChange}
                                className="hidden"
                                id="fileInput"
                            />
                            <label
                                htmlFor="fileInput"
                                className="cursor-pointer flex flex-col items-center gap-2"
                            >
                                <FaFileExcel className="text-4xl text-green-500" />
                                <span className="text-gray-600">
                                    {file ? file.name : 'Click to select CSV or Excel file'}
                                </span>
                                <span className="text-sm text-gray-400">
                                    Supported formats: .csv, .xlsx, .xls
                                </span>
                            </label>
                        </div>
                        
                        <button
                            onClick={handleUpload}
                            disabled={!file || uploading}
                            className="w-full btn-gradient py-3 rounded-lg text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {uploading ? (
                                <>
                                    <FaSpinner className="animate-spin" /> Uploading...
                                </>
                            ) : (
                                <>
                                    <FaUpload /> Upload Tasks
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Right Panel - Instructions */}
                <div className="glass-card p-6">
                    <h2 className="text-xl font-semibold mb-4">Instructions</h2>
                    <div className="space-y-3 text-gray-600">
                        <p>📝 <strong>Required Columns:</strong></p>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                            <li><strong>Title</strong> - Task title (required)</li>
                            <li><strong>Description</strong> - Task description</li>
                            <li><strong>Assigned To Mobile</strong> - User's mobile number (required)</li>
                            <li><strong>Department</strong> - Department name</li>
                            <li><strong>Priority</strong> - Low, Medium, High</li>
                            <li><strong>Frequency</strong> - One Time, Daily, Weekly, Monthly</li>
                        </ul>
                        
                        <p className="mt-4">📅 <strong>For One Time tasks:</strong></p>
                        <ul className="list-disc list-inside ml-4">
                            <li>Due Date - YYYY-MM-DD format</li>
                        </ul>
                        
                        <p className="mt-4">🔄 <strong>For Recurring tasks:</strong></p>
                        <ul className="list-disc list-inside ml-4">
                            <li>Start Date - YYYY-MM-DD format</li>
                            <li>Days Count (for Daily) - Number of days</li>
                            <li>Day of Week (for Weekly) - Monday, Tuesday, etc.</li>
                            <li>Weeks Count (for Weekly) - Number of weeks</li>
                            <li>Day of Month (for Monthly) - 1-31</li>
                            <li>Months Count (for Monthly) - Number of months</li>
                        </ul>
                        
                        <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                            <p className="text-yellow-800 text-sm">
                                ⚠️ <strong>Note:</strong> Users must exist in the system before assigning tasks to them.
                                Sundays and public holidays are automatically skipped for recurring tasks.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Results Section */}
            {results && (
                <div className="mt-6 glass-card p-6">
                    <h2 className="text-xl font-semibold mb-4">Import Results</h2>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-green-50 p-4 rounded-lg text-center">
                            <FaCheckCircle className="text-green-500 text-2xl mx-auto mb-2" />
                            <p className="text-2xl font-bold text-green-600">{results.success.length}</p>
                            <p className="text-gray-600">Successful</p>
                        </div>
                        <div className="bg-red-50 p-4 rounded-lg text-center">
                            <FaTimesCircle className="text-red-500 text-2xl mx-auto mb-2" />
                            <p className="text-2xl font-bold text-red-600">{results.failed.length}</p>
                            <p className="text-gray-600">Failed</p>
                        </div>
                    </div>

                    {results.success.length > 0 && (
                        <div className="mb-4">
                            <h3 className="font-semibold text-green-600 mb-2">✅ Successful Imports:</h3>
                            <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-3">
                                {results.success.map((item, idx) => (
                                    <p key={idx} className="text-sm text-gray-600">• {item.title}</p>
                                ))}
                            </div>
                        </div>
                    )}

                    {results.failed.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-red-600 mb-2">❌ Failed Imports:</h3>
                            <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-3">
                                {results.failed.map((item, idx) => (
                                    <div key={idx} className="mb-2">
                                        <p className="text-sm font-medium text-red-600">• {item.title}</p>
                                        <p className="text-xs text-gray-500 ml-4">Error: {item.error}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BulkUpload;